import React, { useContext, useEffect, useState, useCallback } from 'react';
import AdminContext from '../../../Context/Admin/adminContext';
import '../UserManagement/UserManagement.css';
import usePagination from '../../../hooks/usePagination';
import usePermissions from '../../../hooks/usePermissions';
import {
  PaginationControls,
  FormDetailModal,
  UserSelectionCard,
  EmailStatusBadge,
  PdfDownloadButton,
  ResendEmailButton,
} from './components';
import { extractFormToken } from '../../../Services/adminApiService';

// Fixed list of supported form types
const FORM_TYPES = [
  'mileage',
  'bonus',
  'backpay',
  'hotel',
  'pto',
  'newhire',
  'perdiem',
  'termination',
  'targetorder',
  'uniform',
  'workticket',
  'nonmnmileage',
];

// Map lowercase API form names to registry keys for display names
const FORM_TYPE_DISPLAY = {
  mileage: 'Mileage',
  bonus: 'Bonus',
  backpay: 'BackPay',
  hotel: 'Hotel',
  pto: 'PTO',
  newhire: 'New Hire',
  perdiem: 'Per Diem',
  termination: 'Termination',
  targetorder: 'Target Order',
  uniform: 'Uniform',
  workticket: 'Work Ticket',
  nonmnmileage: 'Non MN Mileage',
  timeadjustment: 'Time Adjustment',
};

const SUBMITTED_FORM_LIST_KEYS = {
  mileage: 'mileageList',
  bonus: 'bonusList',
  backpay: 'backPayList',
  hotel: 'hotelList',
  pto: 'ptoList',
  newhire: 'newHireList',
  perdiem: 'perDiemList',
  termination: 'terminationList',
  targetorder: 'targetOrderList',
  uniform: 'uniformList',
  workticket: 'workTicketList',
  nonmnmileage: 'nonMnMileageList',
  timeadjustment: 'timeAdjustmentList',
};

const getFormDisplayName = (formType) =>
  FORM_TYPE_DISPLAY[formType] ||
  formType?.charAt(0).toUpperCase() + formType?.slice(1) ||
  'Unknown';

const normalizeFormTypeKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const isPageLikeObject = (value) =>
  Boolean(value) && typeof value === 'object' && Array.isArray(value.content);

const extractPageCandidates = (data) =>
  Object.entries(data || {}).filter(([, value]) => isPageLikeObject(value));

const toNumberOrFallback = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const resolveSelectedPage = (data, selectedFormType) => {
  if (!data) {
    return null;
  }

  if (Array.isArray(data?.content)) {
    return { pageKey: null, page: data };
  }

  const candidates = extractPageCandidates(data);
  if (candidates.length === 0) {
    return null;
  }

  const targetKey = normalizeFormTypeKey(selectedFormType || 'mileage');
  const matchedCandidate = candidates.find(([pageKey, page]) => {
    const normalizedPageKey = normalizeFormTypeKey(pageKey);
    const normalizedPageType = normalizeFormTypeKey(
      page?.formType || page?.formName || page?.type
    );

    const matchesPageKey =
      normalizedPageKey &&
      (normalizedPageKey.includes(targetKey) ||
        targetKey.includes(normalizedPageKey));
    const matchesPageType =
      normalizedPageType &&
      (normalizedPageType.includes(targetKey) ||
        targetKey.includes(normalizedPageType));

    return matchesPageKey || matchesPageType;
  });

  if (matchedCandidate) {
    return { pageKey: matchedCandidate[0], page: matchedCandidate[1] };
  }

  return null;
};

const resolveSubmittedFormsList = (data, selectedFormType) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.content)) {
    return data.content;
  }

  const normalizedSelectedFormType = normalizeFormTypeKey(
    selectedFormType || 'mileage'
  );
  const directKeys = [
    SUBMITTED_FORM_LIST_KEYS[normalizedSelectedFormType],
    `${normalizedSelectedFormType}List`,
    normalizedSelectedFormType,
  ].filter(Boolean);

  for (const key of directKeys) {
    const value = data[key];
    if (Array.isArray(value)) {
      return value;
    }
    if (isPageLikeObject(value)) {
      return value.content;
    }
  }

  const matchedCandidate = Object.entries(data).find(([key, value]) => {
    if (!value) {
      return false;
    }
    const normalizedKey = normalizeFormTypeKey(key);
    return (
      normalizedKey.includes(normalizedSelectedFormType) ||
      normalizedSelectedFormType.includes(normalizedKey)
    );
  });

  if (matchedCandidate) {
    const value = matchedCandidate[1];
    if (Array.isArray(value)) {
      return value;
    }
    if (isPageLikeObject(value)) {
      return value.content;
    }
  }

  return [];
};

const extractPageMeta = (page) => {
  if (!page) {
    return {
      currentPage: 0,
      totalPages: 0,
      totalElements: 0,
      pageSize: 20,
      hasNext: false,
      hasPrevious: false,
    };
  }

  return {
    currentPage: toNumberOrFallback(page.currentPage, 0),
    totalPages: toNumberOrFallback(page.totalPages, 0),
    totalElements: toNumberOrFallback(page.totalElements, 0),
    pageSize: toNumberOrFallback(page.pageSize, 20),
    hasNext: Boolean(page.hasNext),
    hasPrevious: Boolean(page.hasPrevious),
  };
};

const sortForms = (forms, sortBy, sortDirection) => {
  const direction = sortDirection === 'asc' ? 1 : -1;
  const normalizedSortBy = sortBy || 'id';

  return [...forms].sort((left, right) => {
    const leftValue =
      normalizedSortBy === 'submittedDate'
        ? new Date(left.created || left.submittedDate || 0).getTime()
        : normalizedSortBy === 'id'
        ? Number(left.id) || 0
        : String(left?.[normalizedSortBy] || '').toLowerCase();
    const rightValue =
      normalizedSortBy === 'submittedDate'
        ? new Date(right.created || right.submittedDate || 0).getTime()
        : normalizedSortBy === 'id'
        ? Number(right.id) || 0
        : String(right?.[normalizedSortBy] || '').toLowerCase();

    if (leftValue < rightValue) {
      return -1 * direction;
    }
    if (leftValue > rightValue) {
      return 1 * direction;
    }
    return 0;
  });
};

const EMAIL_STATUS_OPTIONS = ['ALL', 'SENT', 'FAILED', 'PENDING'];

// Fields to exclude from form details display (signatures contain large base64 data)
const EXCLUDED_FIELDS = [
  'sig',
  'signature',
  'managerSig',
  'employeeSig',
  'managerSignature',
  'employeeSignature',
];

/**
 * Check if a field name is a signature field that should be hidden
 */
const isSignatureField = (key) => {
  const lowerKey = key.toLowerCase();
  return (
    EXCLUDED_FIELDS.some((field) => lowerKey === field.toLowerCase()) ||
    lowerKey.includes('signature') ||
    (lowerKey.includes('sig') && !lowerKey.includes('sign'))
  );
};

const FormsManagement = ({ disableAutoFetch = false }) => {
  const adminContext = useContext(AdminContext);
  const {
    submittedForms,
    paginatedForms,
    currentPage: contextCurrentPage,
    totalPages: contextTotalPages,
    totalElements: contextTotalElements,
    pageSize: contextPageSize,
    hasNext: contextHasNext,
    hasPrevious: contextHasPrevious,
    getFormsPaginated,
    getFormsPaginatedByPortalUser,
    getSubmittedForms,
    portalUserList,
    getAllUsers,
    formLoading,
  } = adminContext;

  // Use centralized permissions hook
  const {
    portalUser,
    isAdmin: hasAdminPermission,
    hasOfficeAccess: hasOfficePermission,
    isDM,
    isAS,
  } = usePermissions();

  const [selectedFormType, setSelectedFormType] = useState('mileage');
  const [selectedUser, setSelectedUser] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [selectedForm, setSelectedForm] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [clientPage, setClientPage] = useState(0);
  const { size, setPageSize } = usePagination({ page: 0, size: 20 });

  // Enhanced filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [emailStatusFilter, setEmailStatusFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  /**
   * Sanitize form data for display by removing signature fields
   * Signatures contain large base64 encoded image data that clutters the UI
   */
  const sanitizeFormForDisplay = useCallback((form) => {
    if (!form) return form;
    const sanitized = {};
    for (const [key, value] of Object.entries(form)) {
      if (!isSignatureField(key)) {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      // Skip auto-fetching during tests to avoid hanging on async effects
      return;
    }
    const fetchData = async () => {
      if (portalUser) {
        // All roles now use the paginated endpoint; backend scopes
        // results based on permission (DM vs Admin/Office).
        if (!isDM && !isAS && selectedUser !== 'all') {
          await getFormsPaginatedByPortalUser(selectedUser, {
            page: 0,
            size,
            sortBy,
            sortDirection,
            formName: (selectedFormType || 'mileage').toLowerCase(),
          });
        } else {
          await getFormsPaginated({
            page: 0,
            size,
            sortBy,
            sortDirection,
            formName: (selectedFormType || 'mileage').toLowerCase(),
          });
        }

        // Only load user list for Office and Admin
        if (!portalUserList && (hasAdminPermission || hasOfficePermission)) {
          await getAllUsers();
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    portalUser,
    size,
    sortBy,
    sortDirection,
    selectedFormType,
    selectedUser,
    isAS,
  ]);

  // Refetch on user change handled in main effect; no client mode state

  const selectedPageInfo = resolveSelectedPage(
    paginatedForms,
    selectedFormType
  );

  const selectedPageMeta = extractPageMeta(selectedPageInfo?.page);

  const serverPaginationMeta = {
    currentPage: toNumberOrFallback(
      contextCurrentPage,
      selectedPageMeta.currentPage
    ),
    totalPages: toNumberOrFallback(
      contextTotalPages,
      selectedPageMeta.totalPages
    ),
    totalElements: toNumberOrFallback(
      contextTotalElements,
      selectedPageMeta.totalElements
    ),
    pageSize: toNumberOrFallback(contextPageSize, selectedPageMeta.pageSize),
    hasNext:
      typeof contextHasNext === 'boolean'
        ? contextHasNext
        : selectedPageMeta.hasNext,
    hasPrevious:
      typeof contextHasPrevious === 'boolean'
        ? contextHasPrevious
        : selectedPageMeta.hasPrevious,
  };

  const selectedPageForms = Array.isArray(selectedPageInfo?.page?.content)
    ? selectedPageInfo.page.content.map((form) => ({
        ...form,
        formType: normalizeFormTypeKey(
          form.formType ||
            selectedPageInfo?.page?.formType ||
            selectedFormType ||
            'mileage'
        ),
      }))
    : [];

  const selectedFullForms = resolveSubmittedFormsList(
    submittedForms,
    selectedFormType
  ).map((form) => ({
    ...form,
    formType: normalizeFormTypeKey(
      form.formType || selectedFormType || 'mileage'
    ),
  }));

  const formCounts = selectedPageInfo?.page
    ? {
        [normalizeFormTypeKey(
          selectedPageInfo.page.formType || selectedFormType || 'mileage'
        )]: selectedPageMeta.totalElements || 0,
      }
    : {};

  const getEmptyStateMessage = () => {
    if (isAS) {
      return 'No submissions found for your assigned sub-areas.';
    }
    if (isDM) {
      return 'No submissions found for your district(s).';
    }
    return 'No forms found';
  };

  const hasClientFilters = Boolean(
    searchQuery.trim() || emailStatusFilter !== 'ALL' || dateFrom || dateTo
  );

  const shouldLoadSubmittedFormsForFilters = Boolean(
    !disableAutoFetch &&
      viewMode === 'table' &&
      hasClientFilters &&
      submittedForms == null
  );

  useEffect(() => {
    if (!shouldLoadSubmittedFormsForFilters || formLoading) {
      return;
    }

    getSubmittedForms();
  }, [shouldLoadSubmittedFormsForFilters, formLoading, getSubmittedForms]);

  const filterSourceForms = hasClientFilters
    ? submittedForms != null
      ? selectedFullForms
      : disableAutoFetch
      ? selectedPageForms
      : []
    : selectedPageForms;

  const filteredForms = (() => {
    let filtered = filterSourceForms;

    if (selectedUser !== 'all') {
      filtered = filtered.filter(
        (form) => form.portalUser?.id?.toString() === selectedUser
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((form) => {
        const id = String(form.id || '').toLowerCase();
        const userName = (form.portalUser?.userName || '').toLowerCase();
        const fullName = `${form.portalUser?.userFirst || ''} ${
          form.portalUser?.userLast || ''
        }`.toLowerCase();
        return id.includes(q) || userName.includes(q) || fullName.includes(q);
      });
    }

    if (emailStatusFilter !== 'ALL') {
      filtered = filtered.filter(
        (form) => (form.emailStatus || 'PENDING') === emailStatusFilter
      );
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter((form) => {
        const d = new Date(form.created || form.submittedDate);
        return d >= from;
      });
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter((form) => {
        const d = new Date(form.created || form.submittedDate);
        return d <= to;
      });
    }

    return sortForms(filtered, sortBy, sortDirection);
  })();

  const filteredTotalPages =
    filteredForms.length > 0 ? Math.ceil(filteredForms.length / size) : 0;
  const safeClientPage =
    filteredTotalPages > 0 ? Math.min(clientPage, filteredTotalPages - 1) : 0;
  const visibleForms = hasClientFilters
    ? filteredForms.slice(safeClientPage * size, (safeClientPage + 1) * size)
    : filteredForms;

  const paginationMeta = hasClientFilters
    ? {
        currentPage: safeClientPage,
        totalPages: filteredTotalPages,
        totalElements: filteredForms.length,
        pageSize: size,
        hasNext: safeClientPage < filteredTotalPages - 1,
        hasPrevious: safeClientPage > 0,
      }
    : serverPaginationMeta;

  useEffect(() => {
    setClientPage(0);
  }, [searchQuery, emailStatusFilter, dateFrom, dateTo, selectedFormType]);

  // Compute result range for display
  const rangeStart =
    paginationMeta.totalElements > 0
      ? paginationMeta.currentPage * paginationMeta.pageSize + 1
      : 0;
  const rangeEnd =
    paginationMeta.totalElements > 0
      ? Math.min(
          (paginationMeta.currentPage + 1) * paginationMeta.pageSize,
          paginationMeta.totalElements
        )
      : 0;

  const hasActiveFilters =
    searchQuery.trim() || emailStatusFilter !== 'ALL' || dateFrom || dateTo;

  const clearAllFilters = () => {
    setSearchQuery('');
    setEmailStatusFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setClientPage(0);
  };

  const goToPage = (page) => {
    const max = paginationMeta.totalPages || 0;
    if (page >= 0 && page < max) {
      if (hasClientFilters) {
        setClientPage(page);
        return;
      }

      if (!isDM && !isAS && selectedUser !== 'all') {
        getFormsPaginatedByPortalUser(selectedUser, {
          page,
          size,
          sortBy,
          sortDirection,
          formName: (selectedFormType || 'mileage').toLowerCase(),
        });
      } else {
        getFormsPaginated({
          page,
          size,
          sortBy,
          sortDirection,
          formName: (selectedFormType || 'mileage').toLowerCase(),
        });
      }
    }
  };

  const nextPage = () => {
    const cp = paginationMeta.currentPage;
    const hn = paginationMeta.hasNext;
    if (hn) {
      goToPage(cp + 1);
    }
  };

  const previousPage = () => {
    const cp = paginationMeta.currentPage;
    const hp = paginationMeta.hasPrevious;
    if (hp) {
      goToPage(cp - 1);
    }
  };

  const changePageSize = (newSize) => {
    setPageSize(newSize);
    if (!isDM && !isAS && selectedUser !== 'all') {
      getFormsPaginatedByPortalUser(selectedUser, {
        page: 0,
        size: newSize,
        sortBy,
        sortDirection,
        formName: (selectedFormType || 'mileage').toLowerCase(),
      });
    } else {
      getFormsPaginated({
        page: 0,
        size: newSize,
        sortBy,
        sortDirection,
        formName: (selectedFormType || 'mileage').toLowerCase(),
      });
    }
  };

  const changeSorting = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    if (!isDM && !isAS && selectedUser !== 'all') {
      getFormsPaginatedByPortalUser(selectedUser, {
        page: 0,
        size,
        sortBy: field,
        sortDirection: direction,
        formName: (selectedFormType || 'mileage').toLowerCase(),
      });
    } else {
      getFormsPaginated({
        page: 0,
        size,
        sortBy: field,
        sortDirection: direction,
        formName: (selectedFormType || 'mileage').toLowerCase(),
      });
    }
  };

  const handleViewForm = (form, formType) => {
    setSelectedForm({ ...form, formType });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedForm(null);
  };

  return (
    <div className="user-management">
      {(formLoading || shouldLoadSubmittedFormsForFilters) && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading forms...</p>
        </div>
      )}
      <div className="management-header">
        <h1>Forms Management</h1>
        <p className="management-subtitle">
          View and manage all form submissions
        </p>
      </div>

      {/* Total Count Card */}
      <div className="section-card" style={{ marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div
              style={{
                fontSize: '48px',
                fontWeight: '700',
                color: '#3182ce',
                marginBottom: '8px',
              }}
            >
              {selectedPageMeta.totalElements || 0}
            </div>
            <div
              style={{ fontSize: '16px', color: '#718096', fontWeight: '500' }}
            >
              Total Form Submissions
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className={viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setViewMode('grid')}
            >
              📊 Grid View
            </button>
            <button
              className={viewMode === 'table' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setViewMode('table')}
            >
              📋 Table View
            </button>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {
        <div className="section-card" style={{ marginBottom: '24px' }}>
          <h2>Page Controls</h2>
          <div
            style={{
              display: 'flex',
              gap: '20px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div
              className="form-group"
              style={{ marginBottom: 0, minWidth: '150px' }}
            >
              <label htmlFor="pageSize">Items per page:</label>
              <select
                id="pageSize"
                value={size}
                onChange={(e) => changePageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div
              className="form-group"
              style={{ marginBottom: 0, minWidth: '150px' }}
            >
              <label htmlFor="sortBy">Sort by:</label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => changeSorting(e.target.value, sortDirection)}
              >
                <option value="id">ID</option>
                <option value="submittedDate">Submission Date</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Direction:</label>
              <button
                className="btn-secondary"
                onClick={() =>
                  changeSorting(
                    sortBy,
                    sortDirection === 'asc' ? 'desc' : 'asc'
                  )
                }
                style={{ marginTop: '4px' }}
              >
                {sortDirection === 'asc' ? '↑ Ascending' : '↓ Descending'}
              </button>
            </div>
          </div>
        </div>
      }

      {/* User Selection Card - Show when filtering by user */}
      {!isDM && !isAS && selectedUser !== 'all' && (
        <UserSelectionCard
          selectedUser={selectedUser}
          portalUserList={portalUserList}
          onClear={() => {
            setSelectedUser('all');
            getFormsPaginated({
              page: 0,
              size,
              sortBy,
              sortDirection,
              formName: (selectedFormType || 'mileage').toLowerCase(),
            });
          }}
        />
      )}

      {/* Filters */}
      <div className="section-card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h2 style={{ margin: 0 }}>Filters</h2>
          {hasActiveFilters && (
            <button
              className="btn-secondary"
              onClick={clearAllFilters}
              style={{ fontSize: '13px', padding: '6px 14px' }}
            >
              ✕ Clear Filters
            </button>
          )}
        </div>
        {(isDM || isAS) && (
          <p style={{ color: '#718096', marginBottom: '16px' }}>
            {isAS
              ? 'Showing only your assigned sub-area submissions'
              : 'Showing only your submitted forms'}
          </p>
        )}

        {/* Row 1: Form Type + User Filter */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isDM || isAS ? '1fr' : '1fr 1fr',
            gap: '20px',
            marginBottom: '16px',
          }}
        >
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="formTypeFilter">Form Type</label>
            <select
              id="formTypeFilter"
              value={selectedFormType}
              onChange={(e) => {
                const newType = e.target.value;
                setSelectedFormType(newType);
                if (!isDM && !isAS && selectedUser !== 'all') {
                  getFormsPaginatedByPortalUser(selectedUser, {
                    page: 0,
                    size,
                    sortBy,
                    sortDirection,
                    formName: (newType || 'mileage').toLowerCase(),
                  });
                } else {
                  getFormsPaginated({
                    page: 0,
                    size,
                    sortBy,
                    sortDirection,
                    formName: (newType || 'mileage').toLowerCase(),
                  });
                }
              }}
            >
              {FORM_TYPES.map((formType) => (
                <option key={formType} value={formType}>
                  {getFormDisplayName(formType)} ({formCounts[formType] ?? 0})
                </option>
              ))}
            </select>
          </div>

          {!isDM && !isAS && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="userFilter">Filter by User</label>
              <select
                id="userFilter"
                value={selectedUser}
                onChange={(e) => {
                  const newUser = e.target.value;
                  setSelectedUser(newUser);
                  if (newUser !== 'all') {
                    getFormsPaginatedByPortalUser(newUser, {
                      page: 0,
                      size,
                      sortBy,
                      sortDirection,
                      formName: (selectedFormType || 'mileage').toLowerCase(),
                    });
                  } else {
                    getFormsPaginated({
                      page: 0,
                      size,
                      sortBy,
                      sortDirection,
                      formName: (selectedFormType || 'mileage').toLowerCase(),
                    });
                  }
                }}
                className={selectedUser !== 'all' ? 'select-active' : ''}
              >
                <option value="all">All Users</option>
                {portalUserList
                  ?.slice()
                  ?.sort((a, b) => {
                    const aName = `${a?.userFirst || ''} ${a?.userLast || ''} ${
                      a?.userName || ''
                    }`
                      .trim()
                      .toLowerCase();
                    const bName = `${b?.userFirst || ''} ${b?.userLast || ''} ${
                      b?.userName || ''
                    }`
                      .trim()
                      .toLowerCase();
                    if (aName < bName) return -1;
                    if (aName > bName) return 1;
                    return 0;
                  })
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.userFirst} {user.userLast} ({user.userName})
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>

        {/* Row 2: Search + Email Status + Date Range */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gap: '20px',
          }}
        >
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="searchFilter">Search</label>
            <input
              id="searchFilter"
              type="text"
              placeholder="ID, username, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{ padding: '12px 16px' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="emailStatusFilter">Email Status</label>
            <select
              id="emailStatusFilter"
              value={emailStatusFilter}
              onChange={(e) => setEmailStatusFilter(e.target.value)}
              className={emailStatusFilter !== 'ALL' ? 'select-active' : ''}
            >
              {EMAIL_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === 'ALL'
                    ? 'All Statuses'
                    : status.charAt(0) + status.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="dateFrom">From Date</label>
            <input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ padding: '12px 16px' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="dateTo">To Date</label>
            <input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ padding: '12px 16px' }}
            />
          </div>
        </div>
      </div>

      {/* Forms Display */}
      {viewMode === 'grid' ? (
        <div className="section-card">
          <h2>Forms by Type</h2>
          {Object.keys(formCounts).length === 0 && (
            <div style={{ color: '#718096', marginBottom: '16px' }}>
              {getEmptyStateMessage()}
            </div>
          )}
          <div className="forms-grid">
            {Object.keys(formCounts).map((formType, index) => {
              const count = formCounts[formType];

              if (count === 0) return null;

              return (
                <div
                  key={index}
                  className="form-type-card"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="form-type-header">
                    <span className="form-type-name">
                      {getFormDisplayName(formType)}
                    </span>
                    <span className="form-type-count">{count}</span>
                  </div>
                  <button
                    className="btn-action"
                    style={{ marginTop: '16px', width: '100%' }}
                    onClick={() => {
                      setSelectedFormType(formType);
                      setViewMode('table');
                    }}
                  >
                    View Forms
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="section-card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div>
              <h2 style={{ margin: '0 0 4px 0' }}>
                {getFormDisplayName(selectedFormType)} Forms
              </h2>
              <p style={{ color: '#718096', margin: 0, fontSize: '14px' }}>
                {paginationMeta.totalElements > 0
                  ? `Showing ${rangeStart}\u2013${rangeEnd} of ${paginationMeta.totalElements} results`
                  : 'No results'}
                {hasActiveFilters &&
                  filteredForms.length > visibleForms.length && (
                    <span style={{ marginLeft: '8px', color: '#3182ce' }}>
                      ({filteredForms.length} matching filters)
                    </span>
                  )}
              </p>
            </div>
            <span style={{ color: '#718096', fontSize: '13px' }}>
              Page {paginationMeta.currentPage + 1} of{' '}
              {paginationMeta.totalPages || 1}
            </span>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Submitted By</th>
                  <th>Date</th>
                  <th>Email Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleForms.length > 0 ? (
                  visibleForms.map((form, formIndex) => {
                    const tokenInfo = extractFormToken(form.pdfDownloadUrl);
                    const submittedDate = form.created || form.submittedDate;
                    const formattedDate = submittedDate
                      ? new Date(submittedDate).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : 'Unknown';
                    const formattedTime = submittedDate
                      ? new Date(submittedDate).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '';
                    return (
                      <tr key={form.id || formIndex}>
                        <td>
                          <span className="username-badge">#{form.id}</span>
                        </td>
                        <td>
                          <span className="form-type-badge">
                            {getFormDisplayName(form.formType)}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {form.portalUser?.userFirst &&
                              form.portalUser?.userLast
                                ? `${form.portalUser.userFirst} ${form.portalUser.userLast}`
                                : form.portalUser?.userName || 'Unknown'}
                            </div>
                            {form.portalUser?.userFirst &&
                              form.portalUser?.userName && (
                                <div
                                  style={{ fontSize: '12px', color: '#718096' }}
                                >
                                  @{form.portalUser.userName}
                                </div>
                              )}
                          </div>
                        </td>
                        <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                          <div>{formattedDate}</div>
                          {formattedTime && (
                            <div style={{ fontSize: '11px', color: '#a0aec0' }}>
                              {formattedTime}
                            </div>
                          )}
                        </td>
                        <td>
                          {form.emailStatus ? (
                            <EmailStatusBadge status={form.emailStatus} />
                          ) : (
                            <span
                              style={{ color: '#a0aec0', fontStyle: 'italic' }}
                            >
                              N/A
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="form-action-buttons">
                            <button
                              className="btn-view"
                              onClick={() =>
                                handleViewForm(form, form.formType)
                              }
                            >
                              View Details
                            </button>
                            {form.pdfDownloadUrl && (
                              <PdfDownloadButton
                                pdfDownloadUrl={form.pdfDownloadUrl}
                                formName={form.formType}
                              />
                            )}
                            {form.emailStatus === 'FAILED' && tokenInfo && (
                              <ResendEmailButton
                                formType={tokenInfo.formType}
                                formToken={tokenInfo.formToken}
                                onStatusUpdate={(newStatus) => {
                                  form.emailStatus = newStatus;
                                }}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#718096',
                      }}
                    >
                      <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                        {hasActiveFilters
                          ? 'No forms match your filters'
                          : getEmptyStateMessage()}
                      </div>
                      {hasActiveFilters && (
                        <button
                          className="btn-secondary"
                          onClick={clearAllFilters}
                          style={{ fontSize: '13px' }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {(paginationMeta.totalPages || 0) > 0 && (
            <PaginationControls
              currentPage={paginationMeta.currentPage}
              totalPages={paginationMeta.totalPages || 0}
              totalElements={paginationMeta.totalElements || 0}
              pageSize={paginationMeta.pageSize || size}
              hasNext={paginationMeta.hasNext}
              hasPrevious={paginationMeta.hasPrevious}
              onPageChange={goToPage}
              onNext={nextPage}
              onPrevious={previousPage}
            />
          )}
        </div>
      )}

      {/* Form Details Modal */}
      <FormDetailModal
        isOpen={showModal}
        form={selectedForm}
        onClose={handleCloseModal}
        sanitizeForm={sanitizeFormForDisplay}
      />
    </div>
  );
};

export default FormsManagement;
